import type { ErrorInfo, ReactNode, FC } from "react"
import React, { Component } from "react"

import { useRecoilValue } from "recoil"

import type { Design } from "~/containers/Proposal/state/designs/design-model"
import { designDiffState } from "~/containers/Proposal/state/designs/design-state"
import { proposalDiffState } from "~/containers/Proposal/state/proposal/proposal-state"
import { siteDiffState } from "~/containers/Proposal/state/site/site-state"
import { AirbrakeContext } from "~/contexts"
import type { GetSiteQuery, Proposal } from "~/graphql/client"
import IS_DEV from "~/utils/isDev"

import { DefaultFallback } from "./DefaultFallback"
import type { FallbackProps } from "./DefaultFallback"

type ClockState = {
  error?: Error | string
  errorInfo?: ErrorInfo
}

type InnerErrorBoundaryProps = {
  [key: string]: unknown
  design: Partial<Design> | null
  site: Partial<GetSiteQuery[`site`]> | null
  proposal: Partial<Proposal> | null
  children: ReactNode
  Fallback?: FC<FallbackProps>
}

class InnerErrorBoundary extends Component<InnerErrorBoundaryProps, ClockState> {
  public static contextType = AirbrakeContext
  declare public context: React.ContextType<typeof AirbrakeContext>
  public constructor(public props: InnerErrorBoundaryProps) {
    super(props)
    this.state = { error: undefined, errorInfo: undefined }
    // We can filter or add information
    // to airbrake notifications here:
    if (this.context?.airbrake) {
      this.context.airbrake.addFilter((notice) => {
        if (IS_DEV) {
          // don't send dev errors
          return null
        }

        return notice
      })
    }
  }

  public componentDidCatch(
    error: ClockState[`error`],
    errorInfo: ClockState[`errorInfo`],
  ): void {
    this.setState({
      error,
      errorInfo,
    })
    // Send error to Airbrake
    if (errorInfo) {
      const state = {
        proposal: this.props.proposal,
        site: this.props.site,
        design: this.props.design,
      }
      if (this.context?.airbrake) {
        try {
          this.context.airbrake.notify({
            context: { info: errorInfo, state },
            error,
          })
        } catch (thrown) {
          console.warn(
            thrown instanceof Error ? thrown.message : thrown,
            errorInfo,
            error,
          )
          throw thrown
        }
      }
    }
  }

  public render(): React.ReactNode {
    const { error, errorInfo } = this.state
    const { children, Fallback = DefaultFallback } = this.props

    return errorInfo ? (
      <Fallback error={error} errorInfo={errorInfo} />
    ) : (
      (children as React.ReactNode)
    )
  }
}

export type ErrorBoundaryProps = {
  children: ReactNode
  Fallback?: FC<FallbackProps>
}

export const RecoilFreeErrorBoundary: FC<ErrorBoundaryProps> = ({
  children,
  Fallback,
}) => {
  return (
    <InnerErrorBoundary
      design={null}
      site={null}
      proposal={null}
      Fallback={Fallback}
    >
      {children}
    </InnerErrorBoundary>
  )
}

export const ErrorBoundary: FC<ErrorBoundaryProps> = ({
  children,
  Fallback,
}) => {
  const design = useRecoilValue(designDiffState)
  const site = useRecoilValue(siteDiffState)
  const proposal = useRecoilValue(proposalDiffState)

  return (
    <InnerErrorBoundary
      design={design}
      site={site}
      proposal={proposal}
      Fallback={Fallback}
    >
      {children}
    </InnerErrorBoundary>
  )
}
